import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ShadedSelect from '../components/ui/ShadedSelect';
import VerifiedEmailBadge from '../components/ui/VerifiedEmailBadge';
import SchoolAffiliationPanel from '../components/widgets/SchoolAffiliationPanel';
import ThemeToggle from '../components/blocks/ThemeToggle';
import LanguageSwitcher from '../components/blocks/LanguageSwitcher';
import {
  formControlClass,
  formErrorBannerClass,
  formInputClass,
  formLabelClass,
  formPrimaryButtonClass,
  formReadonlyClass,
  formSecondaryButtonClass,
  formSuccessBannerClass,
  pageCanvasClass,
  surfaceCardClass,
} from '../components/ui/formUiClasses';
import apiClient from '../utils/apiClient.js';

const PROFILE_TABS = ['profile', 'password', 'settings'];
const SETTINGS_TAB_ALIASES = ['settings', 'accessibility'];

const labelClass = formLabelClass;
const inputClass = formInputClass;
const readonlyFieldClass = formReadonlyClass;
const primaryButtonClass = formPrimaryButtonClass;
const secondaryButtonClass = formSecondaryButtonClass;

const validateNewPassword = (password, t) => {
  if (!password) return t('common.passwordRequired');
  if (password.length < 8) return t('auth.resetPassword.passwordRules');
  const types = [
    /[A-Z]/.test(password),
    /[a-z]/.test(password),
    /\d/.test(password),
    /[@$!%*?&]/.test(password)
  ].filter(Boolean).length;
  if (types < 3) return t('auth.resetPassword.passwordRules');
  return '';
};

function roleLabel(role, t) {
  if (role === 'Tea') return t('report.teacher');
  if (role === 'Sta') return t('report.staff');
  return t('report.student');
}

function PasswordInput({ id, label, value, onChange, show, inputClassName = '' }) {
  const { t } = useTranslation();
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <div className="relative mt-1">
        <input
          id={id}
          name={id}
          type={show ? 'text' : 'password'}
          autoComplete={id === 'currentPassword' ? 'current-password' : 'new-password'}
          value={value}
          onChange={onChange}
          className={`${formControlClass} pr-10 ${inputClassName}`}
        />
        <span className="sr-only">{show ? t('common.hidePassword') : t('common.showPassword')}</span>
      </div>
    </div>
  );
}

function EditProfile() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const tabParam = searchParams.get('tab');
  const activeTab = SETTINGS_TAB_ALIASES.includes(tabParam)
    ? 'settings'
    : PROFILE_TABS.includes(tabParam)
      ? tabParam
      : 'profile';
  const [formData, setFormData] = useState({
    First_Name: '',
    Last_Name: '',
    Nickname: '',
    Form: '',
    Email_Address: '',
    Tel: '',
    School_Name: '',
    School_District: ''
  });
  const [affiliation, setAffiliation] = useState({
    status: 'none',
    method: null,
    school_id: ''
  });
  const [userRole, setUserRole] = useState('Stu');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const hasLoadedDataRef = useRef(false);

  const setActiveTab = (tab) => {
    if (tab === 'profile' || !PROFILE_TABS.includes(tab)) {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (!currentUser || !currentUser.User_ID) {
          setError(t('errors.loginRequired'));
          return;
        }

        if (hasLoadedDataRef.current === currentUser.User_ID) {
          return;
        }

        const response = await apiClient.get('/api/auth/me');
        if (!response.ok) {
          throw new Error('Failed to load profile');
        }
        const userData = await response.json();

        if (userData) {
          setFormData({
            First_Name: userData.First_Name || '',
            Last_Name: userData.Last_Name || '',
            Nickname: userData.Nickname || '',
            Form: userData.Form || '',
            Email_Address: userData.Email_Address || '',
            Tel: userData.Tel || '',
            School_Name: userData.School_Name || '',
            School_District: userData.School_District || ''
          });
          setAffiliation({
            status: userData.affiliation_status || 'none',
            method: userData.affiliation_method || null,
            school_id: userData.school_id || ''
          });
          setUserRole(userData.User_Role || 'Stu');

          hasLoadedDataRef.current = currentUser.User_ID;
        }
      } catch (loadError) {
        setError(t('errors.failedToLoadData'));
      }
    };

    if (currentUser?.User_ID) {
      loadUserData();
    } else {
      hasLoadedDataRef.current = false;
    }
  }, [currentUser?.User_ID, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentUser?.User_ID) {
      setPasswordError(t('errors.loginRequired'));
      return;
    }

    if (!passwordData.currentPassword) {
      setPasswordError(t('auth.changePassword.currentPassword') + ': ' + t('common.passwordRequired'));
      return;
    }

    const newPasswordError = validateNewPassword(passwordData.newPassword, t);
    if (newPasswordError) {
      setPasswordError(newPasswordError);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError(t('common.passwordsDoNotMatch'));
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError(t('auth.changePassword.mustDiffer'));
      return;
    }

    setPasswordLoading(true);
    try {
      const response = await apiClient.post('/api/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(t('auth.changePassword.wrongCurrent'));
        }
        throw new Error(data.message || t('errors.failedToUpdateData'));
      }
      setPasswordSuccess(t('auth.changePassword.success'));
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordError(err.message || t('errors.failedToUpdateData'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (!currentUser || !currentUser.User_ID) {
        setError(t('errors.loginRequired'));
        return;
      }

      const { School_Name, School_District, ...profileFields } = formData;
      const userData = {
        ...profileFields,
        ...(isSchoolVerified ? { School_Name, School_District } : {}),
        needs_recommendation_regeneration: true
      };

      const result = await apiClient.patch('/api/auth/me', userData);
      if (result.ok) {
        setSuccess("Profile updated successfully!");
        setTimeout(() => {
          navigate('/view-report');
        }, 1500);
      } else {
        const errorData = await result.json().catch(() => ({}));
        setError(errorData.message || t('errors.failedToUpdateData'));
      }
    } catch (submitError) {
      setError(t('errors.failedToUpdateData'));
    }
  };

  const handleAffiliationUpdated = (user) => {
    if (!user) return;
    setAffiliation({
      status: user.affiliation_status || 'none',
      method: user.affiliation_method || null,
      school_id: user.school_id || ''
    });
    if (user.School_Name) {
      setFormData((prev) => ({
        ...prev,
        School_Name: user.School_Name,
        School_District: user.School_District || prev.School_District
      }));
    }
    setSuccess(t('school.affiliationAccepted'));
  };

  const isSchoolVerified = affiliation.status === 'verified';

  const tabButtonClass = (tab) => (
    activeTab === tab
      ? 'border-link text-link'
      : 'border-transparent text-ink-subtle hover:border-edge-strong hover:text-ink-muted'
  );

  return (
    <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 ${pageCanvasClass}`}>
      <div className="max-w-3xl mx-auto">
        <div className={`${surfaceCardClass} shadow sm:rounded-lg`}>
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-ink">
              {t('common.editProfile')}
            </h3>

            <nav className="mt-4 -mb-px flex flex-wrap gap-6 border-b border-edge" aria-label={t('common.editProfile')}>
              <button
                type="button"
                onClick={() => setActiveTab('profile')}
                className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${tabButtonClass('profile')}`}
                aria-current={activeTab === 'profile' ? 'page' : undefined}
              >
                {t('common.editProfile')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('password')}
                className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${tabButtonClass('password')}`}
                aria-current={activeTab === 'password' ? 'page' : undefined}
              >
                {t('auth.changePassword.title')}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('settings')}
                className={`whitespace-nowrap border-b-2 py-3 text-sm font-medium transition-colors ${tabButtonClass('settings')}`}
                aria-current={activeTab === 'settings' ? 'page' : undefined}
              >
                {t('common.settings')}
              </button>
            </nav>

            {activeTab === 'profile' ? (
              <div className="mt-6">
                {error && (
                  <div className={`mb-4 ${formErrorBannerClass}`}>
                    {error}
                  </div>
                )}

                {success && (
                  <div className={`mb-4 ${formSuccessBannerClass}`}>
                    {success}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 gap-y-6 gap-x-8 sm:grid-cols-2">
                    <div className="space-y-6">
                      <div>
                        <label htmlFor="First_Name" className={labelClass}>{t('common.firstName')}</label>
                        <input type="text" name="First_Name" id="First_Name" value={formData.First_Name} onChange={handleChange} className={inputClass} />
                      </div>

                      <div>
                        <label htmlFor="Last_Name" className={labelClass}>{t('common.lastName')}</label>
                        <input type="text" name="Last_Name" id="Last_Name" value={formData.Last_Name} onChange={handleChange} className={inputClass} />
                      </div>

                      <div>
                        <label htmlFor="Nickname" className={labelClass}>{t('common.nickname')}</label>
                        <input type="text" name="Nickname" id="Nickname" value={formData.Nickname} onChange={handleChange} className={inputClass} />
                      </div>

                      <div>
                        <label htmlFor="Form" className={labelClass}>{t('common.form')}</label>
                        <ShadedSelect id="Form" name="Form" value={formData.Form} onChange={(v) => handleChange({ target: { name: 'Form', value: v } })} className="mt-1 block w-full" options={[
                          { value: '', label: t('common.selectForm') },
                          { value: '1', label: 'Form 1' },
                          { value: '2', label: 'Form 2' },
                          { value: '3', label: 'Form 3' },
                          { value: '4', label: 'Form 4' },
                          { value: '5', label: 'Form 5' },
                          { value: '6', label: 'Form 6' },
                          { value: 'other', label: t('common.other') },
                        ]} />
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <p className={labelClass}>{t('report.role')}</p>
                        <p className={readonlyFieldClass}>
                          {roleLabel(userRole, t)}
                        </p>
                      </div>

                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <label htmlFor="Email_Address" className={labelClass}>{t('common.email')}</label>
                          <VerifiedEmailBadge
                            affiliationStatus={affiliation.status}
                            affiliationMethod={affiliation.method}
                          />
                        </div>
                        <input type="email" name="Email_Address" id="Email_Address" value={formData.Email_Address} onChange={handleChange} className={inputClass} />
                        {userRole === 'Stu' && affiliation.status !== 'verified' ? (
                          <p className="mt-2 text-xs text-ink-subtle leading-relaxed">
                            {t('school.emailAffiliationHint')}
                          </p>
                        ) : null}
                      </div>

                      <div>
                        <label htmlFor="Tel" className={labelClass}>{t('common.phone')}</label>
                        <input type="tel" name="Tel" id="Tel" value={formData.Tel} onChange={handleChange} className={inputClass} />
                      </div>

                      {isSchoolVerified ? (
                        <>
                          <div>
                            <label htmlFor="School_Name" className={labelClass}>{t('common.schoolName')}</label>
                            <input type="text" name="School_Name" id="School_Name" value={formData.School_Name} onChange={handleChange} className={inputClass} />
                          </div>

                          <div>
                            <label htmlFor="School_District" className={labelClass}>{t('common.schoolDistrict')}</label>
                            <ShadedSelect id="School_District" name="School_District" value={formData.School_District} onChange={(v) => handleChange({ target: { name: 'School_District', value: v } })} className="mt-1 block w-full" options={[
                              { value: '', label: t('common.selectSchoolDistrict') },
                              { value: 'centralAndWestern', label: t('common.districts.centralAndWestern') },
                              { value: 'eastern', label: t('common.districts.eastern') },
                              { value: 'southern', label: t('common.districts.southern') },
                              { value: 'wanChai', label: t('common.districts.wanChai') },
                              { value: 'kowloonCity', label: t('common.districts.kowloonCity') },
                              { value: 'wongTaiSin', label: t('common.districts.wongTaiSin') },
                              { value: 'yauTsimMong', label: t('common.districts.yauTsimMong') },
                              { value: 'shamShuiPo', label: t('common.districts.shamShuiPo') },
                              { value: 'islands', label: t('common.districts.islands') },
                              { value: 'kwaiTsing', label: t('common.districts.kwaiTsing') },
                              { value: 'north', label: t('common.districts.north') },
                              { value: 'saiKung', label: t('common.districts.saiKung') },
                              { value: 'shaTin', label: t('common.districts.shaTin') },
                              { value: 'taiPo', label: t('common.districts.taiPo') },
                              { value: 'tsuenWan', label: t('common.districts.tsuenWan') },
                              { value: 'tuenMun', label: t('common.districts.tuenMun') },
                              { value: 'yuenLong', label: t('common.districts.yuenLong') },
                              { value: 'kwunTong', label: t('common.districts.kwunTong') },
                              { value: 'other', label: t('common.districts.other') },
                            ]} />
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>

                  <SchoolAffiliationPanel
                    affiliationStatus={affiliation.status}
                    affiliationMethod={affiliation.method}
                    schoolName={formData.School_Name}
                    userEmail={formData.Email_Address}
                    userRole={userRole}
                    onUpdated={handleAffiliationUpdated}
                  />

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => navigate('/view-report')}
                      className={secondaryButtonClass}
                    >
                      {t('common.returnToReport')}
                    </button>
                    <button
                      type="submit"
                      className={primaryButtonClass}
                    >
                      {t('common.save')}
                    </button>
                  </div>
                </form>
              </div>
            ) : activeTab === 'password' ? (
              <div className="mt-6">
                <p className="text-sm text-ink-subtle">{t('auth.changePassword.description')}</p>

                {passwordError && (
                  <div className={`mt-4 ${formErrorBannerClass}`} role="alert">
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div className={`mt-4 ${formSuccessBannerClass}`} role="status">
                    {passwordSuccess}
                  </div>
                )}

                <form onSubmit={handlePasswordSubmit} className="mt-4 space-y-4 max-w-md">
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowPasswords((v) => !v)}
                      className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
                      aria-pressed={showPasswords}
                    >
                      {showPasswords ? <FaEyeSlash className="h-4 w-4" aria-hidden /> : <FaEye className="h-4 w-4" aria-hidden />}
                      {showPasswords ? t('common.hidePassword') : t('common.showPassword')}
                    </button>
                  </div>

                  <PasswordInput
                    id="currentPassword"
                    label={t('auth.changePassword.currentPassword')}
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    show={showPasswords}
                  />
                  <PasswordInput
                    id="newPassword"
                    label={t('auth.changePassword.newPassword')}
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    show={showPasswords}
                  />
                  <PasswordInput
                    id="confirmPassword"
                    label={t('auth.changePassword.confirmPassword')}
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    show={showPasswords}
                  />

                  <p className="text-xs text-ink-subtle">{t('auth.resetPassword.passwordRules')}</p>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                    <Link to="/forgot-password" className="text-sm font-medium text-link hover:text-link-hover">
                      {t('auth.changePassword.forgotLink')}
                    </Link>
                    <button
                      type="submit"
                      disabled={passwordLoading}
                      className={`${primaryButtonClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {passwordLoading ? t('common.loading') : t('auth.changePassword.submit')}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="mt-6 space-y-10">
                <section className="space-y-4">
                  <div>
                    <h4 className="text-base font-medium text-ink">{t('common.language')}</h4>
                    <p className="mt-1 text-sm text-ink-muted">{t('common.languageDescription')}</p>
                  </div>
                  <LanguageSwitcher variant="list" />
                </section>

                <section className="space-y-4 border-t border-edge pt-8">
                  <div>
                    <h4 className="text-base font-medium text-ink">{t('common.theme')}</h4>
                    <p className="mt-1 text-sm text-ink-muted">{t('common.themeDescription')}</p>
                  </div>
                  <ThemeToggle />
                </section>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditProfile;
